import { BadgeCheck, Briefcase, CreditCard, Heart, LayoutDashboard, Megaphone, MessageSquare, Repeat, Settings, ShieldCheck, ShoppingBag, UserCircle2, Users, Wallet } from "lucide-react";
import type { DashboardNavItem } from "@/components/DashboardShell";
import type { AppRole } from "@/lib/domainTypes";
import { getAuthState } from "@/lib/authSession";
import { canAccessDashboardBlog } from "@/lib/blogPermissions";

const previewableRoles: AppRole[] = ["user", "lessee", "lessor", "provider", "agent", "oem"];

export const getEffectiveDashboardRole = (authRole: AppRole = "user", previewRole?: string | null): AppRole => {
  if (authRole !== "admin" || !previewRole) return authRole;

  const normalized = previewRole.toLowerCase() as AppRole;
  return previewableRoles.includes(normalized) ? normalized : authRole;
};

const withPreviewRole = (href: string, authRole: AppRole, previewRole?: string | null, previewUser?: string | null) => {
  const effectiveRole = getEffectiveDashboardRole(authRole, previewRole);
  if (authRole !== "admin" || effectiveRole === "admin") return href;
  const params = new URLSearchParams();
  params.set("previewRole", effectiveRole);

  const resolvedPreviewUser = previewUser || (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("previewUser") : null);
  if (resolvedPreviewUser) {
    params.set("previewUser", resolvedPreviewUser);
  }

  return `${href}${href.includes("?") ? "&" : "?"}${params.toString()}`;
};

export const getDashboardPreviewHref = (role: AppRole, previewUser?: string) => {
  const previewRole = role === "admin" ? "user" : role;
  const params = new URLSearchParams({ previewRole });
  if (previewUser) {
    params.set("previewUser", previewUser);
  }
  return previewRole === "agent" ? `/dashboard/agent?${params.toString()}` : `/dashboard?${params.toString()}`;
};

export const getDashboardPreviewLabel = (role: AppRole) => {
  switch (role) {
    case "lessor":
      return "Equipment owner dashboard";
    case "provider":
      return "Service provider dashboard";
    case "agent":
      return "Agent dashboard";
    case "oem":
      return "OEM dashboard";
    case "lessee":
      return "Lessee dashboard";
    case "user":
    default:
      return "Customer dashboard";
  }
};

export const getDashboardNav = (authRole: AppRole = "user", previewRole?: string | null, previewUser?: string | null): DashboardNavItem[] => {
  const role = getEffectiveDashboardRole(authRole, previewRole);
  const to = (href: string) => withPreviewRole(href, authRole, previewRole, previewUser);
  const authEmail = getAuthState().user;
  const includeBlogEditor = canAccessDashboardBlog(authRole, authEmail);

  switch (role) {
    case "agent":
      return [
        { label: "Overview", href: to("/dashboard/agent"), icon: <LayoutDashboard size={16} /> },
        { label: "Tasks", href: to("/dashboard/agent/tasks"), icon: <Briefcase size={16} /> },
        { label: "Get verified", href: to("/dashboard/agent/verification"), icon: <ShieldCheck size={16} /> },
        { label: "Affiliate links", href: to("/dashboard/referrals"), icon: <Users size={16} /> },
        { label: "Earnings", href: to("/dashboard/wallet"), icon: <Wallet size={16} /> },
        { label: "Lease Requests", href: to("/dashboard/lease-requests"), icon: <MessageSquare size={16} /> },
        { label: "Support Chat", href: to("/support?view=conversation"), icon: <MessageSquare size={16} /> },
        ...(includeBlogEditor ? [{ label: "Blog", href: to("/dashboard/blog"), icon: <Megaphone size={16} /> }] : []),
        { label: "Settings", href: to("/dashboard/settings"), icon: <Settings size={16} /> },
      ];
    case "provider":
      return [
        { label: "Overview", href: to("/dashboard"), icon: <LayoutDashboard size={16} /> },
        { label: "Profile", href: to("/dashboard/profile"), icon: <UserCircle2 size={16} /> },
        { label: "My services", href: to("/dashboard/my-services"), icon: <ShoppingBag size={16} /> },
        { label: "Service Requests", href: to("/dashboard/service-requests"), icon: <MessageSquare size={16} /> },
        { label: "Support Chat", href: to("/support?view=conversation"), icon: <MessageSquare size={16} /> },
        { label: "Lease agreements", href: to("/dashboard/lease-agreements"), icon: <ShoppingBag size={16} /> },
        { label: "Payments", href: to("/dashboard/payments"), icon: <CreditCard size={16} /> },
        { label: "Wallet", href: to("/dashboard/wallet"), icon: <Wallet size={16} /> },
        { label: "Referrals", href: to("/dashboard/referrals"), icon: <Repeat size={16} /> },
        { label: "Compliance", href: to("/dashboard/accreditation"), icon: <ShieldCheck size={16} /> },
        { label: "Ads", href: to("/dashboard/ads"), icon: <Megaphone size={16} /> },
        ...(includeBlogEditor ? [{ label: "Blog", href: to("/dashboard/blog"), icon: <Megaphone size={16} /> }] : []),
        { label: "Settings", href: to("/dashboard/settings"), icon: <Settings size={16} /> },
      ];
    case "lessor":
    case "oem":
      return [
        { label: "Overview", href: to("/dashboard"), icon: <LayoutDashboard size={16} /> },
        { label: "Profile", href: to("/dashboard/profile"), icon: <UserCircle2 size={16} /> },
        { label: "My listings", href: to("/dashboard/my-listings"), icon: <ShoppingBag size={16} /> },
        { label: "Lease Requests", href: to("/dashboard/lease-requests"), icon: <MessageSquare size={16} /> },
        { label: "My Requests", href: to("/dashboard/requests"), icon: <MessageSquare size={16} /> },
        { label: "Support Chat", href: to("/support?view=conversation"), icon: <MessageSquare size={16} /> },
        { label: "Lease agreements", href: to("/dashboard/lease-agreements"), icon: <ShoppingBag size={16} /> },
        { label: "My Leases", href: to("/dashboard/rentals"), icon: <ShoppingBag size={16} /> },
        { label: "Payments", href: to("/dashboard/payments"), icon: <CreditCard size={16} /> },
        { label: "Wallet", href: to("/dashboard/wallet"), icon: <Wallet size={16} /> },
        { label: "Compliance", href: to("/dashboard/accreditation"), icon: <ShieldCheck size={16} /> },
        { label: "Verification", href: to("/dashboard/verification"), icon: <BadgeCheck size={16} /> },
        { label: "Ads", href: to("/dashboard/ads"), icon: <Megaphone size={16} /> },
        ...(includeBlogEditor ? [{ label: "Blog", href: to("/dashboard/blog"), icon: <Megaphone size={16} /> }] : []),
        { label: "Settings", href: to("/dashboard/settings"), icon: <Settings size={16} /> },
      ];
    case "lessee":
    case "user":
    default:
      return [
        { label: "Overview", href: to("/dashboard"), icon: <LayoutDashboard size={16} /> },
        { label: "Profile", href: to("/dashboard/profile"), icon: <UserCircle2 size={16} /> },
        { label: "My listings", href: to("/dashboard/my-listings"), icon: <ShoppingBag size={16} /> },
        { label: "My services", href: to("/dashboard/my-services"), icon: <ShoppingBag size={16} /> },
        { label: "Lease Requests", href: to("/dashboard/lease-requests"), icon: <MessageSquare size={16} /> },
        { label: "Purchase Requests", href: to("/dashboard/purchase-requests"), icon: <ShoppingBag size={16} /> },
        { label: "My Requests", href: to("/dashboard/requests"), icon: <MessageSquare size={16} /> },
        { label: "Support Chat", href: to("/support?view=conversation"), icon: <MessageSquare size={16} /> },
        { label: "Lease agreements", href: to("/dashboard/lease-agreements"), icon: <ShoppingBag size={16} /> },
        { label: "Favorites", href: to("/dashboard/favorites"), icon: <Heart size={16} /> },
        { label: "Payments", href: to("/dashboard/payments"), icon: <CreditCard size={16} /> },
        { label: "Wallet", href: to("/dashboard/wallet"), icon: <Wallet size={16} /> },
        { label: "Referrals", href: to("/dashboard/referrals"), icon: <Repeat size={16} /> },
        { label: "Compliance", href: to("/dashboard/accreditation"), icon: <ShieldCheck size={16} /> },
        ...(includeBlogEditor ? [{ label: "Blog", href: to("/dashboard/blog"), icon: <Megaphone size={16} /> }] : []),
        { label: "Settings", href: to("/dashboard/settings"), icon: <Settings size={16} /> },
      ];
  }
};
